import type {
  CmmdBoardTask,
  CmmdMember,
  CmmdProjectAgentMember,
  CmmdProjectTaskAssignmentSubject,
  CmmdTaskStatus,
  CmmdTaskWorkflow,
  CreateCmmdBoardTaskParams,
  UpdateCmmdBoardTaskParams,
} from "@cmmd-forge/contracts";
import type { SidebarThreadSummary } from "~/types";
import { Sheet, SheetPopup, SheetTitle } from "~/components/ui/sheet";
import { RESPONSIVE_ICON_BUTTON_CLASS } from "~/lib/touchTarget";
import { resolveTaskThreadHistory } from "~/lib/taskThreadLink";
import { ProjectTaskDetailEditor } from "./ProjectTaskDetailEditor";
import { useProjectTaskWorkbenchData } from "./useProjectTaskWorkbenchData";
import type { ProjectTaskWorkbenchDataProps } from "./projectTaskWorkbenchTypes";

export type { ProjectTaskWorkbenchDataProps } from "./projectTaskWorkbenchTypes";

const SHEET_CLASS =
  "h-dvh w-full max-w-none overflow-hidden rounded-none border-0 bg-card p-0 text-card-foreground shadow-none md:h-full md:w-[min(82vw,1040px)] md:rounded-xl md:border md:border-border md:shadow-sm md:shadow-black/20";
const SHEET_VIEWPORT_CLASS = "max-md:!p-0";
const EMPTY_AGENT_MEMBERS: readonly CmmdProjectAgentMember[] = [];

async function ignoreAssignment(): Promise<boolean> {
  return false;
}

export interface ProjectTaskDetailProps {
  readonly task: CmmdBoardTask | null;
  readonly projectTitle?: string;
  readonly projectIconUrl?: string | null;
  readonly projectIcon?: string | null;
  readonly allTasks: readonly CmmdBoardTask[];
  readonly members: readonly CmmdMember[];
  readonly agentMembers?: readonly CmmdProjectAgentMember[];
  readonly agentMembersLoading?: boolean;
  readonly agentMembersError?: string | null;
  readonly threadSummaries: readonly SidebarThreadSummary[];
  readonly pending: boolean;
  readonly statusMutationDisabled?: boolean;
  readonly creating: boolean;
  readonly mutationError: string | null;
  readonly startingTaskId: number | null;
  readonly hasKnownConversation?: boolean;
  readonly workflow?: CmmdTaskWorkflow | null;
  readonly onClose: () => void;
  readonly onUpdate: (id: number, patch: UpdateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onSetAssignment?: (
    id: number,
    subject: CmmdProjectTaskAssignmentSubject,
  ) => Promise<boolean>;
  readonly onCreateTask: (params: CreateCmmdBoardTaskParams) => Promise<boolean>;
  readonly onSetStatus: (id: number, status: CmmdTaskStatus, workflowStageId?: string) => void;
  readonly onStartWork: (task: CmmdBoardTask) => Promise<boolean>;
  readonly onOpenThread: (thread: SidebarThreadSummary) => void;
  readonly onOpenTask: (id: number) => void;
  readonly onArchive: (id: number) => Promise<boolean>;
  readonly onUnarchive: (id: number) => void;
  readonly workbenchData?: ProjectTaskWorkbenchDataProps;
}

export function ProjectTaskDetail({
  task,
  projectTitle = "Project",
  projectIconUrl = null,
  projectIcon = null,
  allTasks,
  members,
  agentMembers = EMPTY_AGENT_MEMBERS,
  agentMembersLoading = false,
  agentMembersError = null,
  threadSummaries,
  pending,
  statusMutationDisabled = false,
  creating,
  mutationError,
  startingTaskId,
  hasKnownConversation = false,
  workflow = null,
  onClose,
  onUpdate,
  onSetAssignment = ignoreAssignment,
  onCreateTask,
  onSetStatus,
  onStartWork,
  onOpenThread,
  onOpenTask,
  onArchive,
  onUnarchive,
  workbenchData,
}: ProjectTaskDetailProps) {
  const open = task !== null;
  let liveProjectId: string | null = null;
  if (workbenchData === undefined && task?.projectId != null) {
    liveProjectId = String(task.projectId);
  }
  const liveTaskId = workbenchData ? null : (task?.id ?? null);
  const liveWorkbenchData = useProjectTaskWorkbenchData(liveProjectId, liveTaskId);
  const resolvedWorkbenchData = workbenchData ?? liveWorkbenchData;
  const threadHistory = task ? resolveTaskThreadHistory(threadSummaries, task.id) : [];
  const executing = task !== null && startingTaskId === task.id;

  return (
    <Sheet
      open={open}
      onOpenChange={(next, details) => {
        if (
          !next &&
          details.reason === "escape-key" &&
          document.activeElement?.closest("[data-markdown-editor]")
        ) {
          details.cancel();
          return;
        }
        if (!next) onClose();
      }}
    >
      <SheetPopup
        side="right"
        variant="inset"
        viewportClassName={SHEET_VIEWPORT_CLASS}
        showCloseButton
        className={SHEET_CLASS}
        closeButtonClassName={RESPONSIVE_ICON_BUTTON_CLASS}
      >
        {task ? (
          <>
            <SheetTitle className="sr-only">{task.title}</SheetTitle>
            <ProjectTaskDetailEditor
              key={task.id}
              task={task}
              projectTitle={projectTitle}
              projectIconUrl={projectIconUrl}
              projectIcon={projectIcon}
              allTasks={allTasks}
              members={members}
              agentMembers={agentMembers}
              agentMembersLoading={agentMembersLoading}
              agentMembersError={agentMembersError}
              pending={pending}
              statusMutationDisabled={statusMutationDisabled}
              creating={creating}
              mutationError={mutationError}
              executing={executing}
              hasKnownConversation={hasKnownConversation}
              workflow={workflow}
              threadHistory={threadHistory}
              workbenchData={resolvedWorkbenchData}
              onUpdate={onUpdate}
              onSetAssignment={onSetAssignment}
              onCreateTask={onCreateTask}
              onSetStatus={onSetStatus}
              onStartWork={onStartWork}
              onOpenThread={onOpenThread}
              onOpenTask={onOpenTask}
              onArchive={() => onArchive(task.id)}
              onUnarchive={() => onUnarchive(task.id)}
            />
          </>
        ) : null}
      </SheetPopup>
    </Sheet>
  );
}
